// +----------------------------------------------------------------------
// | 热门纠结 /topics：大家最近在纠结什么
// |
// | 全部是匿名聚合数字 —— 不展示任何人的具体问题，也不需要登录。
// | 这一页的产品意图有两个：
// |   1. 新用户进来先看到「原来大家都在纠结这些」，降低开口门槛
// |   2. 让人知道「你这个纠结很正常」，但**不提供参考答案** ——
// |      别人的比例只是参考，决定权还是自己的
// +----------------------------------------------------------------------
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { hotTopics } from '../../api/topic';
import { toast } from '../../components/Toast';
import { fromNow } from '../../lib/format';
import './Topics.less';

const DAY_OPTIONS = [
  { value: 7, label: '近 7 天' },
  { value: 30, label: '近 30 天' },
  { value: 90, label: '近 90 天' },
];

export default function Topics() {
  const [days, setDays] = useState(30);
  const [activeCat, setActiveCat] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hotTopics({ days });
      setData(res);
    } catch (e) {
      toast(e.message || '榜单没拉到', 'error');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const categories = data?.categories || [];
  const topics = data?.topics || [];

  const shown = useMemo(
    () => (activeCat === '' ? topics : topics.filter((t) => t.category === activeCat)),
    [topics, activeCat]
  );

  if (loading && !data) {
    return <div className="page center muted">正在数一数…</div>;
  }

  return (
    <div className="page topics">
      <h1 className="topics-title">大家最近在纠结什么</h1>
      <p className="topics-sub muted">
        这里只有匿名数字：多少人想过、最后怎么选的。没有任何人的具体问题，也没有 AI 记住的个人信息。
      </p>

      {/* 总览 + 时间窗 */}
      <div className="card topics-head">
        <div className="topics-head-numbers">
          <div className="topics-num">
            <strong>{data?.sample ?? 0}</strong>
            <span className="muted">人把事扔过来了</span>
          </div>
          <div className="topics-num">
            <strong>{data?.decided ?? 0}</strong>
            <span className="muted">人做出了选择</span>
          </div>
        </div>

        <div className="topics-days">
          {DAY_OPTIONS.map((item) => (
            <button
              key={item.value}
              type="button"
              className={`topics-day ${days === item.value ? 'is-active' : ''}`}
              onClick={() => setDays(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {data?.updated_at && (
          <span className="muted topics-updated">统计于 {fromNow(data.updated_at)}</span>
        )}
      </div>

      {/* 分类筛选 */}
      {categories.length > 0 && (
        <div className="topics-filters">
          <button
            type="button"
            className={`topics-filter ${activeCat === '' ? 'is-active' : ''}`}
            onClick={() => setActiveCat('')}
          >
            全部
            <span className="topics-filter-count">{data?.sample ?? 0}</span>
          </button>
          {categories.map((cat) => (
            <button
              key={cat.key}
              type="button"
              className={`topics-filter ${activeCat === cat.key ? 'is-active' : ''}`}
              onClick={() => setActiveCat(cat.key)}
            >
              {cat.emoji} {cat.label}
              <span className="topics-filter-count">{cat.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* 话题榜 */}
      {shown.length === 0 ? (
        <div className="card topics-empty">
          <strong>
            {topics.length === 0 ? '这个时间段还没攒到话题' : '这一类暂时没有上榜的'}
          </strong>
          <span className="muted">
            {topics.length === 0
              ? '换个更长的时间段看看，或者你先扔一件纠结的事过来。'
              : '换个分类，或者把时间窗放宽一点。'}
          </span>
          <Link to="/" className="btn btn-primary btn-sm topics-empty-btn">
            说说我的纠结
          </Link>
        </div>
      ) : (
        <div className="topics-list">
          {shown.map((topic) => (
            <Link to={`/topics/${topic.key}`} key={topic.key} className="card topics-item">
              <div className="topics-item-head">
                <span className="topics-item-emoji">{topic.emoji}</span>
                <span className="topics-item-main">
                  <span className="topics-item-label">{topic.label}</span>
                  <span className="muted topics-item-meta">
                    {topic.count} 人想过
                    {topic.decided > 0 ? ` · ${topic.decided} 人做出了选择` : ' · 还没人做出选择'}
                  </span>
                </span>
                <span className="topics-item-arrow">›</span>
              </div>

              {topic.percent_available ? (
                <>
                  <div className="topics-item-bar">
                    <span style={{ width: `${topic.top_percent || 0}%` }} />
                  </div>
                  <p className="topics-item-summary">
                    最多人选的是
                    <strong>「{topic.top_option}」</strong>
                    <span className="topics-item-pct">{topic.top_percent}%</span>
                  </p>
                </>
              ) : (
                <p className="topics-item-summary muted">
                  还在攒数据 —— {topic.count} 人想过、{topic.decided} 人做出了选择，人再多一点才给占比。
                </p>
              )}
            </Link>
          ))}
        </div>
      )}

      {/* 统计口径说明 */}
      {data?.threshold?.text && (
        <div className="card topics-policy">
          <strong>统计口径</strong>
          <ul>
            <li>{data.threshold.text}</li>
            <li>
              不进统计的：{(data.policy?.excluded || []).join('、')}。
            </li>
            <li>{data.policy?.note}</li>
          </ul>
        </div>
      )}
    </div>
  );
}
